import type { RegressionTableSortableColumns } from '@/composables/useRegressionParams';
import type { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import type { ReturnInterval } from '@/constants/ReturnInterval';
import type { PortfolioVersionType } from '@/constants/PortfolioVersionType';
import { ActiveReturnMode } from '@/types/ActiveReturnMode';
import type { ActiveReturnRebalancingType } from '@/types/ActiveReturnRebalancingType';
import type { RequiredCurrencyConversion } from '@/types/CurrencyConversion';
import type { MonthlyReturnDistributionScaleOption } from '@/types/portfolio';

export type FactorDecompositionPdfRequestParametersBase = {
  pdfTitle: string;
  pdfSubtitle: string;
  pdfAttributes: string;

  period: PeriodAbbrevEnum;
  returnInterval: ReturnInterval;
  startDate: string;
  endDate: string;
  version?: PortfolioVersionType;

  /**
   * If you want to analyse against a particular portfolio tree item, pass that in here.
   * Not applicable for the Combined Module PDF case, because you cannot select an item under analysis
   * that is not the main portfolio
   */
  regresseeId?: string;
  regressionId: string;
  regresseeSlugToUse: string;
  metric: RegressionTableSortableColumns;
  order: number;
  strategyCodes: string[];
  portfolioSlugs: string[];
  scaleOption: MonthlyReturnDistributionScaleOption;
} & RequiredCurrencyConversion;

export type FactorDecompositionTotalReturnPdfParameters = FactorDecompositionPdfRequestParametersBase & {
  mode: ActiveReturnMode.TOTAL_RETURN;
};

export type FactorDecompositionExcessReturnPdfParameters = FactorDecompositionPdfRequestParametersBase & {
  mode: ActiveReturnMode.EXCESS_RETURN;
};

export type FactorDecompositionActiveReturnPdfParameters = FactorDecompositionPdfRequestParametersBase & {
  mode: ActiveReturnMode.ACTIVE_RETURN;
  benchmarkCodeOrSlug: string;
  isPortfolioBenchmark: boolean;
  activeReturnRebalancingType: ActiveReturnRebalancingType;
};

export type FactorDecompositionRegimePdfParameters = FactorDecompositionPdfRequestParametersBase & {
  mode: ActiveReturnMode.TOTAL_RETURN;
  regimeId: string;
};

export type FactorDecompositionPdfParameters =
  | FactorDecompositionTotalReturnPdfParameters
  | FactorDecompositionExcessReturnPdfParameters
  | FactorDecompositionActiveReturnPdfParameters
  | FactorDecompositionRegimePdfParameters;

export type RegressionLocalPdfRequestParameters = FactorDecompositionPdfParameters & {
  code?: string;
};

export const isFactorDecompositionActiveReturnPdfRequest = (
  pdfParams: FactorDecompositionPdfParameters,
): pdfParams is FactorDecompositionActiveReturnPdfParameters => {
  return (pdfParams as FactorDecompositionActiveReturnPdfParameters).mode === ActiveReturnMode.ACTIVE_RETURN;
};

export const isFactorDecompositionExcessReturnPdfRequest = (
  pdfParams: FactorDecompositionPdfParameters,
): pdfParams is FactorDecompositionExcessReturnPdfParameters => {
  return (pdfParams as FactorDecompositionExcessReturnPdfParameters).mode === ActiveReturnMode.EXCESS_RETURN;
};

export const isFactorDecompositionRegimePdfRequest = (
  pdfParams: FactorDecompositionPdfParameters,
): pdfParams is FactorDecompositionRegimePdfParameters => {
  return (pdfParams as FactorDecompositionRegimePdfParameters).regimeId !== undefined;
};
